/* ==========================================================================
   Main Collection
   Desktop filter dropdowns + Section Rendering API swap, and aria-expanded
   state on toolbar trigger buttons. The mobile filter drawer and sort menu
   are owned by global drawer modules. Color swatches on the product card
   navigate to the linked color's product; no JS handler is needed.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-main-collection';
  var CONTAINER_SELECTOR = '.oddit-section-main-collection__container';
  var TRIGGER_SELECTOR =
    '[aria-haspopup="dialog"], [aria-haspopup="listbox"]';
  var FILTER_TOGGLE_SELECTOR = '[data-filter-toggle]';
  var FILTER_PANEL_SELECTOR = '[data-filter-panel]';
  var FILTER_GROUP_SELECTOR = '[data-filter-group]';
  var FILTER_OPTION_SELECTOR = '[data-filter-option]';
  var DRAWER_SELECTOR = '[data-filter-drawer]';
  var DRAWER_OPEN_SELECTOR = '[data-toggle-mobile-filters], [data-toggle-filters]';
  var DRAWER_CLOSE_SELECTOR = '[data-filter-drawer-close]';
  var DRAWER_OPTION_SELECTOR = '[data-drawer-option]';
  var DRAWER_SUBMIT_SELECTOR = '[data-drawer-submit]';
  var DRAWER_OPEN_CLASS = 'oddit-drawer-open';
  var DRAWER_PILL_ACTIVE_CLASS = 'oddit-section-main-collection__filter-drawer-pill--active';
  var DRAWER_OPTION_ACTIVE_CLASS = 'oddit-section-main-collection__filter-option--active';
  function toggleExpanded(button) {
    var expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  }

  /* ----- Filter dropdowns -------------------------------------------------- */

  function closeAllPanels(section) {
    var toggles = section.querySelectorAll(FILTER_TOGGLE_SELECTOR);
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].setAttribute('aria-expanded', 'false');
    }
    var panels = section.querySelectorAll(FILTER_PANEL_SELECTOR);
    for (var j = 0; j < panels.length; j++) {
      panels[j].setAttribute('hidden', '');
    }
  }

  function togglePanel(section, toggle) {
    var handle = toggle.getAttribute('data-filter-handle');
    var panel = section.querySelector(
      '[data-filter-panel="' + handle + '"]'
    );
    if (!panel) {
      // No panel for this filter (e.g. price_range) — just toggle aria.
      toggleExpanded(toggle);
      return;
    }
    var isOpen = toggle.getAttribute('aria-expanded') === 'true';
    closeAllPanels(section);
    if (!isOpen) {
      toggle.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('hidden');
    }
  }

  /* ----- Filter drawer ---------------------------------------------------- */

  function openDrawer(section) {
    var drawer = section.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.removeAttribute('hidden');
    document.body.classList.add(DRAWER_OPEN_CLASS);
    document.documentElement.classList.add(DRAWER_OPEN_CLASS);
    closeAllPanels(section);

    // Always open the first filter section when the drawer opens.
    var firstSection = drawer.querySelector('[data-drawer-section]');
    if (firstSection) firstSection.setAttribute('open', '');
  }

  function closeDrawer(section) {
    var drawer = section.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;
    drawer.setAttribute('hidden', '');
    document.body.classList.remove(DRAWER_OPEN_CLASS);
    document.documentElement.classList.remove(DRAWER_OPEN_CLASS);
  }

  function isDrawerOpen(section) {
    var drawer = section.querySelector(DRAWER_SELECTOR);
    return !!(drawer && !drawer.hasAttribute('hidden'));
  }

  function getOpenDrawerSections(section) {
    var open = [];
    var details = section.querySelectorAll('[data-drawer-section][open]');
    for (var i = 0; i < details.length; i++) {
      open.push(details[i].getAttribute('data-drawer-section'));
    }
    return open;
  }

  function restoreDrawerSections(section, handles) {
    if (!handles || !handles.length) return;
    for (var i = 0; i < handles.length; i++) {
      var el = section.querySelector(
        '[data-drawer-section="' + handles[i] + '"]'
      );
      if (el) el.setAttribute('open', '');
    }
  }

  function isDrawerOptionActive(option) {
    return (
      option.classList.contains(DRAWER_PILL_ACTIVE_CLASS) ||
      option.classList.contains(DRAWER_OPTION_ACTIVE_CLASS)
    );
  }

  function toggleDrawerOption(option) {
    var isPill = option.classList.contains(
      'oddit-section-main-collection__filter-drawer-pill'
    );
    var activeClass = isPill ? DRAWER_PILL_ACTIVE_CLASS : DRAWER_OPTION_ACTIVE_CLASS;
    var willBeActive = !option.classList.contains(activeClass);
    option.classList.toggle(activeClass, willBeActive);
    if (isPill) {
      option.setAttribute('aria-checked', willBeActive ? 'true' : 'false');
    } else {
      option.setAttribute('aria-selected', willBeActive ? 'true' : 'false');
    }
  }

  function buildDrawerUrl(section) {
    var url = new URL(window.location.href);

    // Strip existing filter params and pagination so we start clean.
    var keysToDelete = [];
    url.searchParams.forEach(function (_, key) {
      if (key.indexOf('filter.') === 0) keysToDelete.push(key);
    });
    for (var k = 0; k < keysToDelete.length; k++) {
      url.searchParams.delete(keysToDelete[k]);
    }
    url.searchParams.delete('page');

    var drawer = section.querySelector(DRAWER_SELECTOR);
    if (drawer) {
      var options = drawer.querySelectorAll(DRAWER_OPTION_SELECTOR);
      for (var i = 0; i < options.length; i++) {
        var opt = options[i];
        if (!isDrawerOptionActive(opt)) continue;
        var param = opt.getAttribute('data-filter-param');
        var value = opt.getAttribute('data-filter-value');
        if (param && value) {
          url.searchParams.append(param, value);
        }
      }
    }

    return url;
  }

  function submitDrawer(section) {
    window.location.href = buildDrawerUrl(section).toString();
  }

  function fetchDrawerCount(section) {
    if (!section.id) return;
    var url = buildDrawerUrl(section);
    url.searchParams.set('section_id', section.id);

    var token = (section.__odditCountToken || 0) + 1;
    section.__odditCountToken = token;

    var showBtn = section.querySelector(DRAWER_SUBMIT_SELECTOR);
    if (showBtn) showBtn.setAttribute('data-loading', 'true');

    fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Count fetch failed');
        return r.text();
      })
      .then(function (html) {
        if (section.__odditCountToken !== token) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newShowBtn = doc.querySelector(DRAWER_SUBMIT_SELECTOR);
        var localShowBtn = section.querySelector(DRAWER_SUBMIT_SELECTOR);
        if (newShowBtn && localShowBtn) {
          localShowBtn.textContent = newShowBtn.textContent.trim();
        }
      })
      .catch(function () {})
      .finally(function () {
        if (section.__odditCountToken === token && showBtn) {
          showBtn.removeAttribute('data-loading');
        }
      });
  }

  function scheduleDrawerCountUpdate(section) {
    if (section.__odditCountTimer) {
      clearTimeout(section.__odditCountTimer);
    }
    section.__odditCountTimer = setTimeout(function () {
      fetchDrawerCount(section);
    }, 250);
  }

  /* ----- Section Rendering API swap --------------------------------------- */

  function setLoading(section, loading) {
    var container = section.querySelector(CONTAINER_SELECTOR);
    if (!container) return;
    if (loading) {
      container.setAttribute('data-loading', 'true');
    } else {
      container.removeAttribute('data-loading');
    }
  }

  function applyFilter(section, option) {
    var sectionId = section.id;
    if (!sectionId) return;

    var panel = option.closest(FILTER_PANEL_SELECTOR);
    var isSingleSelect = panel && panel.getAttribute('data-filter-single') === 'true';

    // Sort options preserve current filter params; build URL from window.location.
    var sortValue = option.getAttribute('data-sort-value');
    var targetUrl;
    if (sortValue) {
      targetUrl = new URL(window.location.href);
      targetUrl.searchParams.set('sort_by', sortValue);
      targetUrl.searchParams.delete('page');
    } else {
      var href = option.getAttribute('href');
      if (!href) return;
      try {
        targetUrl = new URL(href, window.location.origin);
      } catch (e) {
        window.location.href = href;
        return;
      }
    }

    // Optimistic toggle so the checkbox reacts immediately.
    var willBeActive = !option.classList.contains('oddit-section-main-collection__filter-option--active');
    if (isSingleSelect) {
      // Single-select: clicking active option is a no-op; otherwise activate it
      // and clear the rest.
      if (!willBeActive) return;
      var siblings = panel.querySelectorAll(FILTER_OPTION_SELECTOR);
      for (var s = 0; s < siblings.length; s++) {
        siblings[s].classList.remove('oddit-section-main-collection__filter-option--active');
        siblings[s].setAttribute('aria-selected', 'false');
      }
      option.classList.add('oddit-section-main-collection__filter-option--active');
      option.setAttribute('aria-selected', 'true');
      // Reflect the new label in the trigger button immediately.
      var group = option.closest(FILTER_GROUP_SELECTOR);
      var triggerLabel = group && group.querySelector('.oddit-section-main-collection__sort-label');
      var optionLabel = option.querySelector('.oddit-section-main-collection__filter-option-label');
      if (triggerLabel && optionLabel) {
        triggerLabel.textContent = optionLabel.textContent;
      }
    } else {
      option.classList.toggle('oddit-section-main-collection__filter-option--active', willBeActive);
      option.setAttribute('aria-selected', willBeActive ? 'true' : 'false');
    }

    // Remember which dropdown was open so we can reopen it after the swap.
    var openGroup = section.querySelector(FILTER_GROUP_SELECTOR + ' [data-filter-toggle][aria-expanded="true"]');
    var openHandle = openGroup ? openGroup.getAttribute('data-filter-handle') : null;

    // Remember drawer state so we can restore it after the swap.
    var drawerWasOpen = isDrawerOpen(section);
    var openDrawerSections = drawerWasOpen ? getOpenDrawerSections(section) : null;

    var fetchUrl = new URL(targetUrl.toString());
    fetchUrl.searchParams.set('section_id', sectionId);

    setLoading(section, true);

    var requestToken = (section.__odditFilterToken || 0) + 1;
    section.__odditFilterToken = requestToken;

    fetch(fetchUrl.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then(function (html) {
        if (section.__odditFilterToken !== requestToken) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newContainer = doc.querySelector(CONTAINER_SELECTOR);
        var oldContainer = section.querySelector(CONTAINER_SELECTOR);
        if (newContainer && oldContainer) {
          oldContainer.replaceWith(newContainer);
        }
        var cleanPath = targetUrl.pathname + targetUrl.search + targetUrl.hash;
        window.history.pushState({}, '', cleanPath);

        // Reopen the dropdown the user was just interacting with.
        if (openHandle) {
          var newToggle = section.querySelector(
            '[data-filter-toggle][data-filter-handle="' + openHandle + '"]'
          );
          if (newToggle) togglePanel(section, newToggle);
        }

        // Restore drawer state if it was open.
        if (drawerWasOpen) {
          openDrawer(section);
          restoreDrawerSections(section, openDrawerSections);
        }
      })
      .catch(function () {
        if (section.__odditFilterToken !== requestToken) return;
        window.location.href = targetUrl.toString();
      })
      .finally(function () {
        if (section.__odditFilterToken === requestToken) {
          setLoading(section, false);
        }
      });
  }

  /* ----- Event handlers --------------------------------------------------- */

  function handleClick(event) {
    var section = event.currentTarget;

    var drawerClose = event.target.closest(DRAWER_CLOSE_SELECTOR);
    if (drawerClose && section.contains(drawerClose)) {
      event.preventDefault();
      closeDrawer(section);
      return;
    }

    var drawerOpen = event.target.closest(DRAWER_OPEN_SELECTOR);
    if (drawerOpen && section.contains(drawerOpen)) {
      event.preventDefault();
      openDrawer(section);
      return;
    }

    var drawerSubmit = event.target.closest(DRAWER_SUBMIT_SELECTOR);
    if (drawerSubmit && section.contains(drawerSubmit)) {
      event.preventDefault();
      submitDrawer(section);
      return;
    }

    var drawerOption = event.target.closest(DRAWER_OPTION_SELECTOR);
    if (drawerOption && section.contains(drawerOption)) {
      event.preventDefault();
      toggleDrawerOption(drawerOption);
      scheduleDrawerCountUpdate(section);
      return;
    }

    var option = event.target.closest(FILTER_OPTION_SELECTOR);
    if (option && section.contains(option)) {
      event.preventDefault();
      var container = section.querySelector(CONTAINER_SELECTOR);
      if (container && container.getAttribute('data-loading') === 'true') return;
      applyFilter(section, option);
      return;
    }

    var filterToggle = event.target.closest(FILTER_TOGGLE_SELECTOR);
    if (filterToggle && section.contains(filterToggle)) {
      togglePanel(section, filterToggle);
      return;
    }

    var trigger = event.target.closest(TRIGGER_SELECTOR);
    if (trigger) toggleExpanded(trigger);
  }

  function initSection(section) {
    if (!section || section.dataset.odditInit === 'true') return;
    section.dataset.odditInit = 'true';
    section.addEventListener('click', handleClick);
  }

  /* ----- Global listeners (once) ------------------------------------------ */

  function initGlobal() {
    if (document.documentElement.dataset.odditMainCollectionGlobal === 'true') return;
    document.documentElement.dataset.odditMainCollectionGlobal = 'true';

    document.addEventListener('click', function (event) {
      if (event.target.closest(FILTER_GROUP_SELECTOR)) return;
      var sections = document.querySelectorAll(SECTION_SELECTOR);
      for (var i = 0; i < sections.length; i++) {
        closeAllPanels(sections[i]);
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var sections = document.querySelectorAll(SECTION_SELECTOR);
      for (var i = 0; i < sections.length; i++) {
        if (isDrawerOpen(sections[i])) {
          closeDrawer(sections[i]);
        } else {
          closeAllPanels(sections[i]);
        }
      }
    });
  }

  initGlobal();

  var sections = document.querySelectorAll(SECTION_SELECTOR);
  for (var i = 0; i < sections.length; i++) {
    initSection(sections[i]);
  }
})();
